import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Users, Building2, Package, FileCheck, Briefcase, AlertTriangle, Clock, CheckCircle, XCircle, Filter, ChevronRight, ExternalLink, StopCircle, Eye, Download, Truck, BarChart3, Receipt, TrendingUp, Flag, FileText, GitBranch, Scale, Activity, MessageSquare, Megaphone, GraduationCap, Edit, LogOut, ChevronDown, ArrowLeft, Settings, Cpu, Database, Zap, FileEdit, Mail, ClipboardCheck, UserCheck, PauseCircle, Link2, CreditCard, Copy } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Sidebar from '@/components/Sidebar';
import NotificationCenter from '@/components/shared/NotificationCenter';
import ClientsTab from '@/components/agency/ClientsTab';
import PackagesTab from '@/components/agency/PackagesTab';
import TeamTab from '@/components/agency/TeamTab';
import CasesTab from '@/components/agency/CasesTab';
import CaseDetailView from '@/components/agency/CaseDetailView';
import InsufficiencyTab from '@/components/agency/InsufficiencyTab';
import VendorTab from '@/components/agency/VendorTab';
import ClientInvoices from '@/components/agency/ClientInvoices';
import CheckDetailsView from '@/components/checks/CheckDetailsView';
import TeamVendorReports from '@/components/reports/TeamVendorReports';
import { generateDashboardPDF } from '@/utils/pdfExport';
import { ExtraCostRequestsTab } from '@/components/admin/ExtraCostRequestsTab';
import { isStoppedCase } from '@/utils/caseFilters';

// Import modular admin components
import {
  AnalyticsTab,
  PriorityManagementTab,
  StandardReportsTab,
  TeamStructureTab,
  WorkloadBalancingTab,
  UserActivityTab,
  ChatTemplatesTab,
  AnnouncementsTab,
  StoppedCasesTab,
  InstitutionsTab,
  CompaniesTab,
  ProposalTemplatesTab
} from '@/components/admin';

const CHECK_NAMES = {
  'address_check': 'Address',
  'criminal_court_check': 'Criminal Court',
  'id_check': 'ID Check',
  'employment_check': 'Employment',
  'education_check': 'Education',
  'credit_check': 'Credit',
  'personal_reference': 'Personal Ref',
  'professional_reference': 'Professional Ref',
  'world_criminal_db': 'World Criminal',
  'india_criminal_db': 'India Criminal',
  'drug_test': 'Drug Test'
};

export default function AgencyDashboard({ user, onLogout, onSwitchAccount, onViewAs, isViewingAs, onReturnToMaster, masterSession }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('');
  const [detailView, setDetailView] = useState(null);
  const [selectedCase, setSelectedCase] = useState(null);
  const [caseDetails, setCaseDetails] = useState(null);
  const [loadingCase, setLoadingCase] = useState(false);
  const [selectedCheck, setSelectedCheck] = useState(null);
  const [clientsList, setClientsList] = useState([]);
  
  // Date filter state
  const [dateFilter, setDateFilter] = useState('all');
  
  // Drill-down modal state
  const [showDrillDown, setShowDrillDown] = useState(false);
  const [drillDownTitle, setDrillDownTitle] = useState('');
  const [drillDownData, setDrillDownData] = useState([]);

  useEffect(() => { fetchStats(); fetchAnalytics(); fetchClients(); }, []);
  useEffect(() => { fetchAnalytics(); }, [selectedClient, selectedAgent]);

  const fetchStats = async () => {
    try {
      let url = '/dashboard/stats';
      if (selectedClient) url += '?client_id=' + selectedClient;
      const res = await axios.get(url);
      setStats(res.data);
    } catch (e) { toast.error('Failed to load statistics'); }
    finally { setLoading(false); }
  };
  
  // Refetch stats when client filter changes
  useEffect(() => { if (selectedClient !== '') fetchStats(); }, [selectedClient]);

  const fetchClients = async () => {
    try {
      const res = await axios.get('/clients');
      setClientsList(res.data || []);
    } catch (e) { console.log('Clients load failed'); }
  };

  const fetchAnalytics = async () => {
    try {
      let url = '/dashboard/analytics';
      const params = [];
      if (selectedClient) params.push('client_id=' + selectedClient);
      if (selectedAgent) params.push('agent_id=' + selectedAgent);
      if (params.length > 0) url += '?' + params.join('&');
      const res = await axios.get(url);
      setAnalytics(res.data);
    } catch (e) { 
      console.error('Analytics load failed:', e.response?.data || e.message);
      toast.error(e.response?.data?.detail || 'Failed to load analytics data');
    }
  };

  // Date filter helper
  const getDateFilteredCases = (cases) => {
    if (dateFilter === 'all' || !cases) return cases || [];
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    return cases.filter(c => {
      const caseDate = new Date(c.created_at);
      switch (dateFilter) {
        case 'today':
          return caseDate >= today;
        case 'week':
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 7);
          return caseDate >= weekAgo;
        case 'month':
          const monthAgo = new Date(today);
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          return caseDate >= monthAgo;
        case 'year':
          const yearAgo = new Date(today);
          yearAgo.setFullYear(yearAgo.getFullYear() - 1);
          return caseDate >= yearAgo;
        default:
          return true;
      }
    });
  };

  // Client filter helper - filter cases by selected client
  const getClientFilteredCases = (cases) => {
    if (!selectedClient || !cases) return cases || [];
    return cases.filter(c => c.client_id === selectedClient);
  };

  // Combined filter (date + client)
  const getFilteredCases = (cases) => {
    return getDateFilteredCases(getClientFilteredCases(cases));
  };

  const openDrillDown = (title, data) => {
    setDrillDownTitle(title);
    // Sort data by created_at date, latest first
    const sortedData = [...(data || [])].sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateB - dateA; // Latest first
    });
    setDrillDownData(sortedData);
    setShowDrillDown(true);
  };

  const getStatusBadge = (status) => {
    const config = {
      'pending': { class: 'bg-slate-100 text-slate-700', label: 'Pending' },
      'pending_qc': { class: 'bg-amber-100 text-amber-700', label: 'Pending QC' },
      'qc_approved': { class: 'bg-emerald-100 text-emerald-700', label: 'QC Approved' },
      'in_progress': { class: 'bg-blue-100 text-blue-700', label: 'In Progress' },
      'completed': { class: 'bg-emerald-100 text-emerald-700', label: 'Completed' },
      'final_report_qc': { class: 'bg-purple-100 text-purple-700', label: 'Final Report QC' },
      'report_approved': { class: 'bg-emerald-100 text-emerald-700', label: 'Report Approved' },
      'stopped': { class: 'bg-red-100 text-red-700', label: 'Stopped' }
    };
    const c = config[status] || config['pending'];
    return <Badge className={c.class}>{c.label}</Badge>;
  };

  // Grouped Menu Items for organized sidebar
  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Briefcase },
    { id: 'analytics', label: 'Analytics', icon: TrendingUp },
    { id: 'cases', label: 'All Cases', icon: FileCheck },
    
    // Verification Status Reports Group
    {
      id: 'reports-group',
      label: 'Verification Reports',
      icon: FileText,
      isGroup: true,
      items: [
        { id: 'standard-reports', label: 'Status Report', icon: FileText },
        { id: 'stopped-cases', label: 'Stopped Cases', icon: StopCircle },
        { id: 'insufficiencies', label: 'Insufficiencies', icon: AlertTriangle },
        { id: 'extra-cost', label: 'Extra Cost Requests', icon: CreditCard },
        { id: 'priority', label: 'Priority Cases', icon: Flag },
        { id: 'edit-requests', label: 'Edit Requests', icon: Edit },
      ]
    },
    
    // Team Management Group
    {
      id: 'team-group',
      label: 'Team Management',
      icon: Users,
      isGroup: true,
      items: [
        { id: 'team', label: 'Verification Team', icon: Users },
        { id: 'team-structure', label: 'Team Structure', icon: GitBranch },
        { id: 'workload', label: 'Workload Balancing', icon: Scale },
        { id: 'user-activity', label: 'User Activity', icon: Activity },
        { id: 'reports', label: 'Team & Vendor Reports', icon: BarChart3 },
      ]
    },
    
    // Finance Group
    {
      id: 'finance-group',
      label: 'Finance',
      icon: Receipt,
      isGroup: true,
      items: [
        { id: 'client-invoices', label: 'Client Invoices', icon: Receipt },
        { id: 'packages', label: 'Packages & Pricing', icon: Package },
      ]
    },
    
    // Client & Vendor Group
    {
      id: 'clients-vendors-group',
      label: 'Clients & Vendors',
      icon: Building2,
      isGroup: true,
      items: [
        { id: 'clients', label: 'Clients', icon: Building2 },
        { id: 'vendors', label: 'Vendors', icon: Truck },
      ]
    },
    
    // Resources Group
    {
      id: 'resources-group',
      label: 'Resources',
      icon: Database,
      isGroup: true,
      items: [
        { id: 'institutions', label: 'Universities & Institutions', icon: GraduationCap },
        { id: 'companies', label: 'Company Database', icon: Building2 },
        { id: 'proposal-templates', label: 'Proposal Templates', icon: FileText },
        { id: 'chat-templates', label: 'Chat Templates', icon: MessageSquare },
        { id: 'announcements', label: 'Announcements', icon: Megaphone },
      ]
    },
    
    // Admin Settings Group
    {
      id: 'admin-group',
      label: 'Admin Settings',
      icon: Settings,
      isGroup: true,
      items: [
        { id: 'ai-settings', label: 'AI Features', icon: Zap, isLink: true, href: '/admin/settings' },
        { id: 'integrations', label: 'External Integrations', icon: Link2, isLink: true, href: '/admin/integrations' },
        { id: 'migrations', label: 'DB Migrations', icon: Database, isLink: true, href: '/admin/migrations' },
      ]
    },
  ];

  // Stopped cases state
  const [allCases, setAllCases] = useState([]);
  const [stoppedReasonFilter, setStoppedReasonFilter] = useState('all');
  const [stoppedDateFrom, setStoppedDateFrom] = useState('');
  const [stoppedDateTo, setStoppedDateTo] = useState('');

  const fetchAllCases = async () => {
    try {
      const res = await axios.get('/cases');
      setAllCases(res.data || []);
    } catch (e) { console.log('Cases load failed'); }
  };

  useEffect(() => { 
    // Load all cases on component mount for dashboard stats
    fetchAllCases();
  }, []);
  
  useEffect(() => { 
    // Reload cases when switching to stopped-cases tab
    if (activeTab === 'stopped-cases') {
      fetchAllCases();
    }
  }, [activeTab]);

  const stoppedCases = allCases.filter(isStoppedCase);

  // Edit Requests state
  const [editRequests, setEditRequests] = useState([]);
  const [editRequestsLoading, setEditRequestsLoading] = useState(false);
  const [editRequestFilter, setEditRequestFilter] = useState('pending');
  const [reviewingRequest, setReviewingRequest] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');

  const fetchEditRequests = async () => {
    setEditRequestsLoading(true);
    try {
      const res = await axios.get('/edit-requests');
      setEditRequests(res.data || []);
    } catch (e) { 
      console.log('Edit requests load failed'); 
    } finally {
      setEditRequestsLoading(false);
    }
  };

  useEffect(() => { 
    if (activeTab === 'edit-requests') {
      fetchEditRequests();
    }
  }, [activeTab]);

  const handleReviewEditRequest = async (requestId, action) => {
    try {
      await axios.put(`/edit-requests/${requestId}/review?action=${action}${reviewNotes ? '&notes=' + encodeURIComponent(reviewNotes) : ''}`);
      toast.success(`Edit request ${action}d successfully`);
      setReviewingRequest(null);
      setReviewNotes('');
      fetchEditRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} request`);
    }
  };

  const getFilteredEditRequests = () => {
    if (editRequestFilter === 'all') return editRequests;
    return editRequests.filter(r => r.status === editRequestFilter);
  };

  const getFilteredStoppedCases = () => {
    let filtered = stoppedCases;
    
    if (stoppedReasonFilter !== 'all') {
      filtered = filtered.filter(c => c.stop_reason === stoppedReasonFilter);
    }
    
    if (stoppedDateFrom) {
      filtered = filtered.filter(c => new Date(c.stopped_at || c.updated_at) >= new Date(stoppedDateFrom));
    }
    
    if (stoppedDateTo) {
      filtered = filtered.filter(c => new Date(c.stopped_at || c.updated_at) <= new Date(stoppedDateTo + 'T23:59:59'));
    }
    
    return filtered;
  };

  const getStopReasonLabel = (reason) => {
    const labels = {
      'candidate_no_show': 'Candidate No Show',
      'terminated': 'Terminated',
      'candidate_withdrew': 'Candidate Withdrew',
      'offer_revoked': 'Offer Revoked',
      'other': 'Other'
    };
    return labels[reason] || reason || 'N/A';
  };

  const openDetailTab = (type, data) => {
    setDetailView({ type, data });
    setSelectedCase(null);
    setCaseDetails(null);
    setActiveTab('detail');
  };

  const viewCaseDetails = async (caseId) => {
    setLoadingCase(true);
    try {
      const caseRes = await axios.get('/cases/' + caseId);
      let empData = null;
      if (caseRes.data.employee_id) {
        try { const empRes = await axios.get('/employees/' + caseRes.data.employee_id); empData = empRes.data; } catch (e) {}
      }
      setCaseDetails({ case: caseRes.data, employee: empData });
      setSelectedCase(caseId);
    } catch (e) { toast.error('Failed to load case'); }
    finally { setLoadingCase(false); }
  };

  const handleViewCase = (caseId) => {
    setDetailView({ type: 'Case Details', data: [] });
    setActiveTab('detail');
    viewCaseDetails(caseId);
  };

  const renderDetailView = () => {
    if (!detailView) return null;
    if (selectedCase && caseDetails) {
      return (<CaseDetailView caseData={caseDetails.case} employee={caseDetails.employee} onBack={() => { setSelectedCase(null); setCaseDetails(null); }} />);
    }
    if (loadingCase) return (<div className="py-20 text-center text-slate-600">Loading case details...</div>);
    
    const { type, data } = detailView;
    return (
      <div>
        <div className="flex items-center gap-2 mb-6">
          <Button variant="outline" size="sm" onClick={() => { setDetailView(null); setActiveTab('dashboard'); }}>← Back to Dashboard</Button>
          <h1 className="text-2xl font-bold text-slate-900">{type}</h1>
          <span className="text-sm text-slate-500 ml-2">(Click row to view complete details)</span>
        </div>
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-4 font-medium text-slate-600">Case ID</th>
                  <th className="text-left p-4 font-medium text-slate-600">Employee</th>
                  <th className="text-left p-4 font-medium text-slate-600">Client</th>
                  <th className="text-left p-4 font-medium text-slate-600">Agent</th>
                  <th className="text-left p-4 font-medium text-slate-600">Days</th>
                  <th className="text-left p-4 font-medium text-slate-600">TAT</th>
                  <th className="text-left p-4 font-medium text-slate-600">Status</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {data.map((item, idx) => (
                  <tr key={idx} className="hover:bg-slate-100 cursor-pointer" onClick={() => viewCaseDetails(item.id)}>
                    <td className="p-4 font-mono text-sm text-blue-600">{item.id}...</td>
                    <td className="p-4">{item.employee_name}</td>
                    <td className="p-4">{item.client_name}</td>
                    <td className="p-4">{item.agent_name}</td>
                    <td className="p-4">{item.days_elapsed}</td>
                    <td className="p-4">{item.tat_days} days</td>
                    <td className="p-4"><Badge className={item.days_elapsed <= item.tat_days ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}>{item.days_elapsed <= item.tat_days ? 'Within TAT' : 'Over TAT'}</Badge></td>
                    <td className="p-4"><ChevronRight className="w-4 h-4 text-slate-400" /></td>
                  </tr>
                ))}
                {data.length === 0 && (<tr><td colSpan="8" className="p-8 text-center text-slate-500">No cases found</td></tr>)}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    );
  };

  const dateFilterLabel = {
    'all': 'All Time',
    'today': 'Today',
    'week': 'This Week',
    'month': 'This Month',
    'year': 'This Year'
  };

  const handleExportPDF = () => {
    try {
      const filteredCases = getFilteredCases(allCases);
      
      generateDashboardPDF({
        title: 'Agency Dashboard Report',
        subtitle: user.name,
        dateRange: dateFilterLabel[dateFilter],
        generatedBy: user.name,
        stats: [
          { label: 'Total Clients', value: stats?.total_clients || 0, color: 'slate' },
          { label: 'Total Cases', value: stats?.total_cases || 0, color: 'slate' },
          { label: 'In Progress', value: stats?.in_progress_cases || 0, color: 'blue' },
          { label: 'Completed', value: stats?.completed_cases || 0, color: 'emerald' },
          { label: 'Insufficiency', value: stats?.insufficiency_cases || 0, color: 'amber' },
          { label: 'Pending QC', value: filteredCases.filter(c => c.status === 'pending_qc').length, color: 'amber' },
          { label: 'Final Report QC', value: filteredCases.filter(c => c.status === 'final_report_qc').length, color: 'purple' },
          { label: 'QC Approved', value: filteredCases.filter(c => c.status === 'qc_approved').length, color: 'emerald' },
          { label: 'Stopped Cases', value: filteredCases.filter(isStoppedCase).length, color: 'rose' },
          { label: 'Within TAT', value: analytics?.tat_summary?.within_tat || 0, color: 'emerald' },
          { label: 'Over TAT', value: analytics?.tat_summary?.over_tat || 0, color: 'rose' }
        ],
        tables: [
          {
            title: 'Client-wise Summary',
            headers: ['Client', 'Total', 'Within TAT', 'Over TAT'],
            rows: analytics?.client_stats ? Object.entries(analytics.client_stats).map(([name, data]) => [
              name,
              data.total,
              data.within_tat,
              data.over_tat
            ]) : []
          }
        ]
      });
      toast.success('PDF report downloaded successfully');
    } catch (error) {
      toast.error('Failed to generate PDF');
      console.error('PDF generation error:', error);
    }
  };

  const renderDashboard = () => (
    <div>
      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Agency Dashboard</h1>
          <p className="text-slate-600">Welcome back, {user.name}</p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            onClick={handleExportPDF}
            className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            data-testid="agency-export-pdf-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
          <div className="flex items-center gap-2">
            <Label className="text-sm text-slate-600">Filter:</Label>
            <Select value={dateFilter} onValueChange={setDateFilter}>
              <SelectTrigger className="w-36" data-testid="agency-date-filter">
                <SelectValue placeholder="Select period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
                <SelectItem value="year">This Year</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Client Filter */}
            <Select value={selectedClient || "all_clients"} onValueChange={(val) => setSelectedClient(val === "all_clients" ? "" : val)}>
              <SelectTrigger className="w-48" data-testid="agency-client-filter">
                <SelectValue placeholder="All Clients" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all_clients">All Clients</SelectItem>
                {clientsList.map(client => (
                  <SelectItem key={client.id} value={client.id}>{client.company_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {(selectedClient || dateFilter !== 'all') && (
              <Button variant="outline" size="sm" onClick={() => { setSelectedClient(''); setDateFilter('all'); }}>
                Clear Filters
              </Button>
            )}
          </div>
        </div>
      </div>

      {(dateFilter !== 'all' || selectedClient) && (
        <div className="mb-4 p-2 bg-blue-50 rounded-lg text-sm text-blue-700 flex items-center gap-4">
          {dateFilter !== 'all' && <span>Period: <strong>{dateFilterLabel[dateFilter]}</strong></span>}
          {selectedClient && <span>Client: <strong>{clientsList.find(c => c.id === selectedClient)?.company_name || selectedClient}</strong></span>}
        </div>
      )}

      {loading ? (<div className="text-slate-600">Loading...</div>) : (
        <div className="space-y-8">
          {/* Primary Stats Row - Verification Progress */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <StatCard icon={PauseCircle} label="Not Initiated" value={stats?.case_overall_not_initiated || getFilteredCases(allCases.filter(c => c.verification_progress === 'not_initiated')).length} color="violet" subtitle="Click to view cases" onClick={() => openDrillDown('Not Initiated Cases', getFilteredCases(allCases.filter(c => c.verification_progress === 'not_initiated')))} />
            <StatCard icon={Clock} label="In Progress" value={stats?.in_progress_cases || 0} color="blue" subtitle="Click to view cases" onClick={() => openDrillDown('In Progress Cases', getFilteredCases(allCases.filter(c => c.verification_progress === 'in_progress')))} />
            <StatCard icon={CheckCircle} label="Completed" value={stats?.completed_cases || 0} color="emerald" subtitle="Click to view cases" onClick={() => openDrillDown('Completed Cases', getFilteredCases(allCases.filter(c => c.verification_progress === 'completed')))} />
            <StatCard icon={AlertTriangle} label="Insufficiency" value={stats?.insufficiency_cases || 0} color="amber" subtitle="Click to view cases" onClick={() => openDrillDown('Insufficiency Cases', getFilteredCases(allCases.filter(c => c.verification_progress === 'insufficiency')))} />
          </div>

          {/* Case Pipeline Stats Row - Draft, Invite Sent, Not Initiated */}
          <div className="bg-slate-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Case Pipeline Status
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              <Card className="border-slate-200 bg-white shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => openDrillDown('Draft Cases', getFilteredCases(allCases.filter(c => c.status === 'initiated' && !c.employee_submitted && (c.employee_details_filled || !c.employee_invited))))}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                      <FileEdit className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Draft</p>
                      <p className="text-xl font-bold text-slate-700">{getFilteredCases(allCases.filter(c => c.status === 'initiated' && !c.employee_submitted && (c.employee_details_filled || !c.employee_invited))).length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-orange-200 bg-orange-50 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => openDrillDown('Invite Sent (Pending)', getFilteredCases(allCases.filter(c => c.employee_invited && !c.employee_details_filled)))}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-xs text-orange-600">Invite Sent</p>
                      <p className="text-xl font-bold text-orange-700">{getFilteredCases(allCases.filter(c => c.employee_invited && !c.employee_details_filled)).length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-cyan-200 bg-cyan-50 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => openDrillDown('Details Filled', getFilteredCases(allCases.filter(c => c.status === 'details_filled')))}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center">
                      <ClipboardCheck className="w-5 h-5 text-cyan-600" />
                    </div>
                    <div>
                      <p className="text-xs text-cyan-600">Details Filled</p>
                      <p className="text-xl font-bold text-cyan-700">{stats?.details_filled_cases || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-indigo-200 bg-indigo-50 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => openDrillDown('Pending Client Approval', getFilteredCases(allCases.filter(c => c.status === 'pending_client_approval')))}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                      <UserCheck className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-xs text-indigo-600">Client Approval</p>
                      <p className="text-xl font-bold text-indigo-700">{stats?.pending_client_approval || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-violet-200 bg-violet-50 shadow-sm cursor-pointer hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center">
                      <PauseCircle className="w-5 h-5 text-violet-600" />
                    </div>
                    <div>
                      <p className="text-xs text-violet-600">Not Initiated</p>
                      <p className="text-xl font-bold text-violet-700">{stats?.not_initiated_checks || 0}</p>
                      <p className="text-xs text-violet-500">checks</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-teal-200 bg-teal-50 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                      <Activity className="w-5 h-5 text-teal-600" />
                    </div>
                    <div>
                      <p className="text-xs text-teal-600">In Progress</p>
                      <p className="text-xl font-bold text-teal-700">{stats?.in_progress_checks || 0}</p>
                      <p className="text-xs text-teal-500">checks</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* QC Stats Row */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="border-amber-200 bg-amber-50 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => openDrillDown('Pending QC Cases', getFilteredCases(allCases.filter(c => c.status === 'pending_qc')))}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <Clock className="w-8 h-8 text-amber-600" />
                  <div>
                    <p className="text-sm text-amber-700">Pending QC</p>
                    <p className="text-2xl font-bold text-amber-600">{getFilteredCases(allCases.filter(c => c.status === 'pending_qc')).length}</p>
                    <p className="text-xs text-amber-600">Click to view</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-purple-200 bg-purple-50 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => openDrillDown('Final Report QC Cases', getFilteredCases(allCases.filter(c => c.status === 'final_report_qc')))}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <FileCheck className="w-8 h-8 text-purple-600" />
                  <div>
                    <p className="text-sm text-purple-700">Final Report QC</p>
                    <p className="text-2xl font-bold text-purple-600">{getFilteredCases(allCases.filter(c => c.status === 'final_report_qc')).length}</p>
                    <p className="text-xs text-purple-600">Click to view</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-emerald-200 bg-emerald-50 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => openDrillDown('QC Approved Cases', getFilteredCases(allCases.filter(c => c.status === 'qc_approved')))}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                  <div>
                    <p className="text-sm text-emerald-700">QC Approved</p>
                    <p className="text-2xl font-bold text-emerald-600">{getFilteredCases(allCases.filter(c => c.status === 'qc_approved')).length}</p>
                    <p className="text-xs text-emerald-600">Click to view</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-red-200 bg-red-50 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => openDrillDown('Stopped Cases', getFilteredCases(allCases.filter(isStoppedCase)))}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <StopCircle className="w-8 h-8 text-red-600" />
                  <div>
                    <p className="text-sm text-red-700">Stopped Cases</p>
                    <p className="text-2xl font-bold text-red-600">{getFilteredCases(allCases.filter(isStoppedCase)).length}</p>
                    <p className="text-xs text-red-600">Click to view</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="border-slate-200">
            <CardHeader className="pb-3"><CardTitle className="text-lg flex items-center gap-2"><Filter className="w-5 h-5" />Filters</CardTitle></CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4">
                <FilterSelect label="Filter by Client" value={selectedClient} onChange={setSelectedClient} options={analytics?.clients?.map(c => ({ id: c.id, name: c.name })) || []} testId="client-filter" />
                <FilterSelect label="Filter by Agent" value={selectedAgent} onChange={setSelectedAgent} options={analytics?.agents?.map(a => ({ id: a.id, name: a.name })) || []} testId="agent-filter" />
                {(selectedClient || selectedAgent) && (<Button variant="outline" onClick={() => { setSelectedClient(''); setSelectedAgent(''); }} className="self-end">Clear Filters</Button>)}
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            <TATCard type="within" count={analytics?.tat_summary?.within_tat || 0} onClick={() => openDetailTab('Cases Within TAT', analytics?.within_tat_cases || [])} />
            <TATCard type="paused" count={analytics?.tat_summary?.paused || 0} onClick={() => openDetailTab('Cases TAT Paused (Insufficiency)', analytics?.paused_tat_cases || [])} />
            <TATCard type="over" count={analytics?.tat_summary?.over_tat || 0} onClick={() => openDetailTab('Cases Over TAT', analytics?.over_tat_cases || [])} />
            {/* Active Clients - clients with at least one active case */}
            <Card className="border-l-4 border-l-blue-500 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => openDrillDown('All Cases', getFilteredCases(allCases))}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Active Clients</p>
                    <p className="text-3xl font-bold text-blue-600">{stats?.active_clients || 0}</p>
                    <p className="text-xs text-slate-500">Clients with cases</p>
                  </div>
                  <Building2 className="w-8 h-8 text-blue-400" />
                </div>
                <p className="text-xs text-blue-600 mt-2">Click to view details →</p>
              </CardContent>
            </Card>
            {/* Total Cases */}
            <Card className="border-l-4 border-l-slate-500 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => openDrillDown('All Cases', getFilteredCases(allCases))}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Total Cases</p>
                    <p className="text-3xl font-bold text-slate-700">{stats?.total_cases || 0}</p>
                    <p className="text-xs text-slate-500">All time</p>
                  </div>
                  <FileCheck className="w-8 h-8 text-slate-400" />
                </div>
                <p className="text-xs text-slate-600 mt-2">Click to view details →</p>
              </CardContent>
            </Card>
          </div>

          <DataTable title="Client-wise Summary" hint="(Case Overall TAT: Submission to Completion, pauses during insufficiency)" headers={['Client', 'Total Cases', 'Within TAT', 'Paused', 'Over TAT', 'Insufficiency', '']}
            data={analytics?.client_stats ? Object.entries(analytics.client_stats) : []}
            renderRow={([name, data]) => {
              const cases = [...(analytics?.within_tat_cases || []), ...(analytics?.over_tat_cases || []), ...(analytics?.paused_tat_cases || [])].filter(c => c.client_name === name);
              return (
                <tr key={name} className="hover:bg-slate-100 cursor-pointer" onClick={() => openDetailTab('Cases for ' + name, cases)}>
                  <td className="p-4 font-medium">{name}</td>
                  <td className="p-4 text-center">{data.total}</td>
                  <td className="p-4 text-center"><Badge className="bg-emerald-100 text-emerald-700">{data.within_tat}</Badge></td>
                  <td className="p-4 text-center"><Badge className={data.paused > 0 ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-600'}>{data.paused || 0}</Badge></td>
                  <td className="p-4 text-center"><Badge className={data.over_tat > 0 ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600'}>{data.over_tat}</Badge></td>
                  <td className="p-4 text-center"><Badge variant="outline" className={data.insufficiency > 0 ? 'border-amber-500 text-amber-600' : ''}>{data.insufficiency}</Badge></td>
                  <td className="p-4"><ChevronRight className="w-4 h-4 text-slate-400" /></td>
                </tr>
              );
            }}
          />

          <DataTable title="Check-wise TAT Status" hint="(TAT starts when QC assigns to Operations, pauses during insufficiency)" headers={['Check Type', 'Total', 'Pending', 'Completed', 'Not Assigned', 'Paused', 'Within TAT', 'Over TAT', '']}
            data={analytics?.check_stats ? Object.entries(analytics.check_stats) : []}
            renderRow={([type, data]) => {
              const cases = [...(analytics?.within_tat_cases || []), ...(analytics?.over_tat_cases || [])];
              return (
                <tr key={type} className="hover:bg-slate-100 cursor-pointer" onClick={() => openDetailTab((CHECK_NAMES[type] || type) + ' - Cases', cases)}>
                  <td className="p-4 font-medium">{CHECK_NAMES[type] || type}</td>
                  <td className="p-4 text-center">{data.total}</td>
                  <td className="p-4 text-center"><Badge className="bg-amber-50 text-amber-700">{data.pending}</Badge></td>
                  <td className="p-4 text-center"><Badge className="bg-emerald-100 text-emerald-700">{data.completed}</Badge></td>
                  <td className="p-4 text-center"><span className="text-slate-500">{data.not_started || 0}</span></td>
                  <td className="p-4 text-center"><span className={data.paused > 0 ? 'text-orange-600 font-medium' : 'text-slate-400'}>{data.paused || 0}</span></td>
                  <td className="p-4 text-center text-emerald-600 font-medium">{data.within_tat}</td>
                  <td className="p-4 text-center"><span className={data.over_tat > 0 ? 'text-rose-600 font-medium' : 'text-slate-400'}>{data.over_tat}</span></td>
                  <td className="p-4"><ChevronRight className="w-4 h-4 text-slate-400" /></td>
                </tr>
              );
            }}
          />

          {analytics?.mirrored_address_count > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700" data-testid="mirrored-address-note">
              <Copy className="w-4 h-4 text-blue-500 flex-shrink-0" />
              <span><strong>{analytics.mirrored_address_count}</strong> permanent address check{analytics.mirrored_address_count > 1 ? 's' : ''} mirrored from present address (Same as Present). These are auto-synced and excluded from vendor assignment.</span>
            </div>
          )}

          <DataTable title="Agent-wise Check Pendency" hint="(Click row to view cases)" headers={['Agent', 'Total Cases', 'Pending', 'Completed', 'Check Pendency', '']}
            data={analytics?.agent_stats ? Object.entries(analytics.agent_stats) : []}
            renderRow={([name, data]) => {
              const cases = [...(analytics?.within_tat_cases || []), ...(analytics?.over_tat_cases || [])].filter(c => c.agent_name === name);
              return (
                <tr key={name} className="hover:bg-slate-100 cursor-pointer" onClick={() => openDetailTab('Cases for ' + name, cases)}>
                  <td className="p-4 font-medium">{name}</td>
                  <td className="p-4 text-center">{data.total}</td>
                  <td className="p-4 text-center"><Badge className="bg-amber-50 text-amber-700">{data.pending}</Badge></td>
                  <td className="p-4 text-center"><Badge className="bg-emerald-100 text-emerald-700">{data.completed}</Badge></td>
                  <td className="p-4">
                    <div className="flex flex-wrap gap-1">
                      {data.checks && Object.entries(data.checks).map(([ct, cd]) => cd.pending > 0 && (<Badge key={ct} variant="outline" className="text-xs">{CHECK_NAMES[ct] || ct}: {cd.pending}</Badge>))}
                      {(!data.checks || Object.values(data.checks).every(c => c.pending === 0)) && <span className="text-sm text-slate-400">No pending</span>}
                    </div>
                  </td>
                  <td className="p-4"><ChevronRight className="w-4 h-4 text-slate-400" /></td>
                </tr>
              );
            }}
          />
        </div>
      )}
    </div>
  );

  const handleViewCheckFromInsuff = (caseId, checkId) => {
    setSelectedCheck({ caseId, checkId });
    setActiveTab('checkDetail');
  };

  // Render Edit Requests Tab
  const renderEditRequests = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Edit Requests</h1>
          <p className="text-slate-500">Review and approve/reject employee data edit requests from HR</p>
        </div>
        <div className="flex items-center gap-4">
          <Select value={editRequestFilter} onValueChange={setEditRequestFilter}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Requests</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchEditRequests}>
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-amber-700">{editRequests.filter(r => r.status === 'pending').length}</div>
            <div className="text-sm text-amber-600">Pending Review</div>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-emerald-700">{editRequests.filter(r => r.status === 'approved').length}</div>
            <div className="text-sm text-emerald-600">Approved</div>
          </CardContent>
        </Card>
        <Card className="border-rose-200 bg-rose-50">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-rose-700">{editRequests.filter(r => r.status === 'rejected').length}</div>
            <div className="text-sm text-rose-600">Rejected</div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-slate-700">{editRequests.length}</div>
            <div className="text-sm text-slate-600">Total Requests</div>
          </CardContent>
        </Card>
      </div>

      {/* Requests Table */}
      <Card>
        <CardContent className="p-0">
          {editRequestsLoading ? (
            <div className="p-8 text-center text-slate-500">Loading edit requests...</div>
          ) : getFilteredEditRequests().length === 0 ? (
            <div className="p-8 text-center text-slate-500">No edit requests found</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Changed Fields</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Requested By</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {getFilteredEditRequests().map((request) => {
                  const changedFields = Object.keys(request.updated_data || {}).filter(
                    key => request.updated_data[key] !== request.original_data?.[key]
                  );
                  return (
                    <TableRow key={request.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{request.original_data?.name || 'N/A'}</p>
                          <p className="text-xs text-slate-500">{request.original_data?.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {changedFields.map(field => (
                            <Badge key={field} variant="outline" className="text-xs">
                              {field.replace(/_/g, ' ')}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-xs truncate">{request.reason}</TableCell>
                      <TableCell className="text-xs">{request.requested_by_name || request.requested_by}</TableCell>
                      <TableCell className="text-xs">{new Date(request.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Badge className={
                          request.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                          request.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                          'bg-rose-100 text-rose-700'
                        }>
                          {request.status?.charAt(0).toUpperCase() + request.status?.slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {request.status === 'pending' ? (
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-300 hover:bg-emerald-50" onClick={() => setReviewingRequest({ ...request, action: 'approve' })}>
                              <CheckCircle className="w-4 h-4 mr-1" /> Approve
                            </Button>
                            <Button size="sm" variant="outline" className="text-rose-600 border-rose-300 hover:bg-rose-50" onClick={() => setReviewingRequest({ ...request, action: 'reject' })}>
                              <XCircle className="w-4 h-4 mr-1" /> Reject
                            </Button>
                          </div>
                        ) : (
                          <Button size="sm" variant="ghost" onClick={() => setReviewingRequest(request)}>
                            <Eye className="w-4 h-4 mr-1" /> View
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog open={!!reviewingRequest} onOpenChange={(o) => { if (!o) { setReviewingRequest(null); setReviewNotes(''); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              {reviewingRequest?.status === 'pending' ? 'Review Edit Request' : 'Edit Request Details'}
            </DialogTitle>
          </DialogHeader>
          
          {/* Action Buttons at Top for Pending Requests */}
          {reviewingRequest?.status === 'pending' && (
            <div className="flex justify-between items-center py-3 px-4 bg-slate-50 rounded-lg border">
              <div>
                <Label className="text-sm">Review Notes (Optional)</Label>
                <Input 
                  value={reviewNotes} 
                  onChange={(e) => setReviewNotes(e.target.value)} 
                  placeholder="Add notes..."
                  className="w-64 mt-1"
                />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="text-rose-600 border-rose-300 hover:bg-rose-50" onClick={() => handleReviewEditRequest(reviewingRequest.id, 'reject')}>
                  <XCircle className="w-4 h-4 mr-2" /> Reject
                </Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handleReviewEditRequest(reviewingRequest.id, 'approve')}>
                  <CheckCircle className="w-4 h-4 mr-2" /> Approve
                </Button>
              </div>
            </div>
          )}
          
          {reviewingRequest && (
            <div className="space-y-4 overflow-y-auto flex-1 pr-2">
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-medium mb-2">Employee: {reviewingRequest.original_data?.name}</h4>
                <p className="text-sm text-slate-500">{reviewingRequest.original_data?.email}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-rose-50 rounded-lg border border-rose-200">
                  <h4 className="font-medium text-rose-700 mb-2">Original Data</h4>
                  <div className="space-y-1 text-sm">
                    {Object.entries(reviewingRequest.original_data || {}).filter(([k]) => !['id', 'case_id', 'entity_id', 'created_at', 'updated_at'].includes(k)).map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <span className="text-slate-500">{key.replace(/_/g, ' ')}:</span>
                        <span className="font-medium">{value || '-'}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <h4 className="font-medium text-emerald-700 mb-2">Updated Data</h4>
                  <div className="space-y-1 text-sm">
                    {Object.entries(reviewingRequest.updated_data || {}).filter(([k]) => !['id', 'case_id', 'entity_id', 'created_at', 'updated_at'].includes(k)).map(([key, value]) => {
                      const isChanged = value !== reviewingRequest.original_data?.[key];
                      return (
                        <div key={key} className={`flex justify-between ${isChanged ? 'bg-emerald-100 px-1 rounded' : ''}`}>
                          <span className="text-slate-500">{key.replace(/_/g, ' ')}:</span>
                          <span className={`font-medium ${isChanged ? 'text-emerald-700' : ''}`}>{value || '-'}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <h4 className="font-medium text-amber-700 mb-1">Reason for Edit</h4>
                <p className="text-sm">{reviewingRequest.reason}</p>
              </div>

              {reviewingRequest.status !== 'pending' && reviewingRequest.review_notes && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <h4 className="font-medium mb-1">Review Notes</h4>
                  <p className="text-sm">{reviewingRequest.review_notes}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="border-t pt-4 mt-4">
            <Button variant="outline" onClick={() => { setReviewingRequest(null); setReviewNotes(''); }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  const renderContent = () => {
    if (activeTab === 'dashboard') return renderDashboard();
    if (activeTab === 'detail') return renderDetailView();
    if (activeTab === 'analytics') return <AnalyticsTab stats={stats} analytics={analytics} />;
    if (activeTab === 'priority') return <PriorityManagementTab stats={stats} />;
    if (activeTab === 'standard-reports') return <StandardReportsTab setActiveTab={setActiveTab} />;
    if (activeTab === 'clients') return <ClientsTab />;
    if (activeTab === 'packages') return <PackagesTab />;
    if (activeTab === 'team') return <TeamTab />;
    if (activeTab === 'team-structure') return <TeamStructureTab user={user} stats={stats} analytics={analytics} />;
    if (activeTab === 'workload') return <WorkloadBalancingTab analytics={analytics} />;
    if (activeTab === 'user-activity') return <UserActivityTab analytics={analytics} stats={stats} />;
    if (activeTab === 'vendors') return <VendorTab />;
    if (activeTab === 'client-invoices') return <ClientInvoices />;
    if (activeTab === 'reports') return <TeamVendorReports />;
    if (activeTab === 'proposal-templates') return <ProposalTemplatesTab />;
    if (activeTab === 'chat-templates') return <ChatTemplatesTab />;
    if (activeTab === 'announcements') return <AnnouncementsTab />;
    if (activeTab === 'institutions') return <InstitutionsTab />;
    if (activeTab === 'companies') return <CompaniesTab />;
    if (activeTab === 'cases') return <CasesTab />;
    if (activeTab === 'stopped-cases') return <StoppedCasesTab onViewCase={handleViewCase} />;
    if (activeTab === 'edit-requests') return renderEditRequests();
    if (activeTab === 'insufficiencies') return <InsufficiencyTab onViewCase={handleViewCheckFromInsuff} />;
    if (activeTab === 'extra-cost') return <ExtraCostRequestsTab />;
    if (activeTab === 'checkDetail' && selectedCheck) return (
      <CheckDetailsView
        caseId={selectedCheck.caseId}
        checkId={selectedCheck.checkId}
        onBack={() => { setSelectedCheck(null); setActiveTab('insufficiencies'); }}
      />
    );
    
    // Default - return dashboard
    return renderDashboard();
  };

  // Analytics Tab
  const renderAnalytics = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-slate-900">Analytics</h1>
        <Button variant="outline" onClick={() => generateDashboardPDF(stats, analytics)}>
          <Download className="w-4 h-4 mr-2" />Export Report
        </Button>
      </div>
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-700">{stats.total_cases || 0}</p>
              <p className="text-sm text-blue-600">Total Cases</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-green-700">{stats.completed_cases || 0}</p>
              <p className="text-sm text-green-600">Completed</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-amber-700">{stats.pending_cases || 0}</p>
              <p className="text-sm text-amber-600">Pending</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-purple-700">{analytics?.check_stats ? Object.keys(analytics.check_stats).length : 0}</p>
              <p className="text-sm text-purple-600">Check Types</p>
            </div>
          </CardContent>
        </Card>
      </div>
      {analytics?.check_stats && (
        <Card>
          <CardHeader><CardTitle>Check Type Performance</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Check Type</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Completed</TableHead>
                  <TableHead>Pending</TableHead>
                  <TableHead>Completion Rate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(analytics.check_stats).map(([type, data]) => (
                  <TableRow key={type}>
                    <TableCell className="font-medium">{CHECK_NAMES[type] || type}</TableCell>
                    <TableCell>{data.total}</TableCell>
                    <TableCell className="text-green-600">{data.completed}</TableCell>
                    <TableCell className="text-amber-600">{data.pending}</TableCell>
                    <TableCell>
                      <Badge className={data.total > 0 && (data.completed / data.total) > 0.7 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                        {data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0}%
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // Priority Management Tab
  const renderPriorityManagement = () => (
    <div>
      <h1 className="text-3xl font-bold text-slate-900 mb-6">Priority Management</h1>
      <div className="grid grid-cols-3 gap-6">
        <Card className="border-red-200 bg-red-50">
          <CardHeader><CardTitle className="flex items-center gap-2 text-red-700"><Flag className="w-5 h-5" />High Priority</CardTitle></CardHeader>
          <CardContent>
            <p className="text-4xl font-bold text-red-700">{stats.high_priority_cases || 0}</p>
            <p className="text-sm text-red-600">Cases requiring immediate attention</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader><CardTitle className="flex items-center gap-2 text-amber-700"><Clock className="w-5 h-5" />Medium Priority</CardTitle></CardHeader>
          <CardContent>
            <p className="text-4xl font-bold text-amber-700">{stats.medium_priority_cases || Math.floor((stats.pending_cases || 0) * 0.4)}</p>
            <p className="text-sm text-amber-600">Standard processing queue</p>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50">
          <CardHeader><CardTitle className="flex items-center gap-2 text-green-700"><CheckCircle className="w-5 h-5" />Low Priority</CardTitle></CardHeader>
          <CardContent>
            <p className="text-4xl font-bold text-green-700">{stats.low_priority_cases || Math.floor((stats.pending_cases || 0) * 0.3)}</p>
            <p className="text-sm text-green-600">Flexible timeline cases</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  // Standard Reports Tab
  const renderStandardReports = () => (
    <div>
      <h1 className="text-3xl font-bold text-slate-900 mb-6">Standard Reports</h1>
      <div className="grid grid-cols-2 gap-6">
        <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setActiveTab('reports')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg"><BarChart3 className="w-6 h-6 text-blue-600" /></div>
              <div>
                <p className="font-semibold">Team & Vendor Reports</p>
                <p className="text-sm text-slate-500">Performance metrics for teams and vendors</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setActiveTab('analytics')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg"><TrendingUp className="w-6 h-6 text-green-600" /></div>
              <div>
                <p className="font-semibold">Analytics Report</p>
                <p className="text-sm text-slate-500">Detailed analytics and trends</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setActiveTab('client-invoices')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg"><Receipt className="w-6 h-6 text-purple-600" /></div>
              <div>
                <p className="font-semibold">Invoice Reports</p>
                <p className="text-sm text-slate-500">Client billing and payment tracking</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setActiveTab('cases')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-lg"><FileCheck className="w-6 h-6 text-amber-600" /></div>
              <div>
                <p className="font-semibold">Case Status Report</p>
                <p className="text-sm text-slate-500">Overview of all verification cases</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  // Team Structure Tab
  const renderTeamStructure = () => (
    <div>
      <h1 className="text-3xl font-bold text-slate-900 mb-6">Team Structure</h1>
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col items-center">
            <div className="p-4 bg-indigo-100 rounded-lg mb-4">
              <Users className="w-8 h-8 text-indigo-600" />
            </div>
            <p className="font-semibold text-lg">Agency Admin</p>
            <p className="text-sm text-slate-500 mb-6">{user.name}</p>
            <div className="w-px h-8 bg-slate-300"></div>
            <div className="grid grid-cols-3 gap-8 mt-4">
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <p className="font-medium text-purple-700">QC Team</p>
                <p className="text-2xl font-bold text-purple-800">{analytics?.agents?.filter(a => a.role === 'qc_operations').length || 1}</p>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="font-medium text-blue-700">Verification Team</p>
                <p className="text-2xl font-bold text-blue-800">{analytics?.agents?.filter(a => a.role === 'verification_team').length || 1}</p>
              </div>
              <div className="text-center p-4 bg-amber-50 rounded-lg">
                <p className="font-medium text-amber-700">Vendors</p>
                <p className="text-2xl font-bold text-amber-800">{stats.total_vendors || 0}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Workload Balancing Tab
  const renderWorkloadBalancing = () => (
    <div>
      <h1 className="text-3xl font-bold text-slate-900 mb-6">Workload Balancing</h1>
      <Card>
        <CardHeader><CardTitle>Team Member Workload</CardTitle></CardHeader>
        <CardContent>
          {analytics?.agent_stats ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Team Member</TableHead>
                  <TableHead>Assigned</TableHead>
                  <TableHead>Completed</TableHead>
                  <TableHead>Pending</TableHead>
                  <TableHead>Workload Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(analytics.agent_stats).map(([name, data]) => {
                  const pending = (data.assigned || 0) - (data.completed || 0);
                  const status = pending > 10 ? 'overloaded' : pending > 5 ? 'busy' : 'available';
                  return (
                    <TableRow key={name}>
                      <TableCell className="font-medium">{name}</TableCell>
                      <TableCell>{data.assigned || 0}</TableCell>
                      <TableCell className="text-green-600">{data.completed || 0}</TableCell>
                      <TableCell className="text-amber-600">{pending}</TableCell>
                      <TableCell>
                        <Badge className={
                          status === 'overloaded' ? 'bg-red-100 text-red-700' :
                          status === 'busy' ? 'bg-amber-100 text-amber-700' :
                          'bg-green-100 text-green-700'
                        }>
                          {status === 'overloaded' ? 'Overloaded' : status === 'busy' ? 'Busy' : 'Available'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <p className="text-slate-500 text-center py-8">No workload data available</p>
          )}
        </CardContent>
      </Card>
    </div>
  );

  // User Activity Tab
  const renderUserActivity = () => (
    <div>
      <h1 className="text-3xl font-bold text-slate-900 mb-6">User Activity</h1>
      <div className="grid grid-cols-2 gap-6 mb-6">
        <Card>
          <CardHeader><CardTitle>Active Users Today</CardTitle></CardHeader>
          <CardContent>
            <p className="text-4xl font-bold text-green-600">{analytics?.agents?.length || 5}</p>
            <p className="text-sm text-slate-500">Users logged in today</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Total System Users</CardTitle></CardHeader>
          <CardContent>
            <p className="text-4xl font-bold text-blue-600">{(analytics?.agents?.length || 5) + (stats.total_vendors || 0) + 2}</p>
            <p className="text-sm text-slate-500">Registered users in system</p>
          </CardContent>
        </Card>
      </div>
      <Card>
        <CardHeader><CardTitle>Recent Activity Log</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <Activity className="w-5 h-5 text-green-600" />
              <div>
                <p className="font-medium">Admin User logged in</p>
                <p className="text-xs text-slate-500">Just now</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <FileCheck className="w-5 h-5 text-blue-600" />
              <div>
                <p className="font-medium">Case CASE001 updated</p>
                <p className="text-xs text-slate-500">5 minutes ago</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <Users className="w-5 h-5 text-purple-600" />
              <div>
                <p className="font-medium">New vendor assigned</p>
                <p className="text-xs text-slate-500">15 minutes ago</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Chat Templates Tab
  const [chatTemplates, setChatTemplates] = useState([
    { id: 1, name: 'Welcome Message', category: 'onboarding', content: 'Welcome to Veecheck! We are here to assist you with your background verification process.' },
    { id: 2, name: 'Document Request', category: 'verification', content: 'We require additional documents for your verification. Please upload the following: [Document List]' },
    { id: 3, name: 'Verification Complete', category: 'completion', content: 'Your background verification has been completed successfully. Thank you for your cooperation.' },
    { id: 4, name: 'Insufficiency Notice', category: 'insufficiency', content: 'We found some discrepancies in your submitted documents. Please provide clarification for: [Issue Details]' },
    { id: 5, name: 'Follow-up Reminder', category: 'reminder', content: 'This is a friendly reminder to submit your pending documents for the verification process.' },
  ]);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  const renderChatTemplates = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-slate-900">Chat Templates</h1>
        <Button onClick={() => { setEditingTemplate({ name: '', category: 'general', content: '' }); setShowTemplateDialog(true); }}>
          <MessageSquare className="w-4 h-4 mr-2" />Add Template
        </Button>
      </div>
      <div className="grid grid-cols-1 gap-4">
        {chatTemplates.map(template => (
          <Card key={template.id} className="hover:shadow-md transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <p className="font-semibold text-lg">{template.name}</p>
                    <Badge className="bg-slate-100 text-slate-700">{template.category}</Badge>
                  </div>
                  <p className="text-slate-600 text-sm">{template.content}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => { setEditingTemplate(template); setShowTemplateDialog(true); }}>
                    Edit
                  </Button>
                  <Button variant="ghost" size="sm" className="text-red-600" onClick={() => setChatTemplates(chatTemplates.filter(t => t.id !== template.id))}>
                    Delete
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {showTemplateDialog && (
        <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingTemplate?.id ? 'Edit Template' : 'Add Template'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Template Name</Label>
                <Input 
                  value={editingTemplate?.name || ''} 
                  onChange={(e) => setEditingTemplate({...editingTemplate, name: e.target.value})}
                  placeholder="e.g., Welcome Message"
                />
              </div>
              <div>
                <Label>Category</Label>
                <select 
                  className="w-full border rounded-md p-2"
                  value={editingTemplate?.category || 'general'}
                  onChange={(e) => setEditingTemplate({...editingTemplate, category: e.target.value})}
                >
                  <option value="general">General</option>
                  <option value="onboarding">Onboarding</option>
                  <option value="verification">Verification</option>
                  <option value="completion">Completion</option>
                  <option value="insufficiency">Insufficiency</option>
                  <option value="reminder">Reminder</option>
                </select>
              </div>
              <div>
                <Label>Message Content</Label>
                <textarea 
                  className="w-full border rounded-md p-2 min-h-[100px]"
                  value={editingTemplate?.content || ''}
                  onChange={(e) => setEditingTemplate({...editingTemplate, content: e.target.value})}
                  placeholder="Enter template message..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>Cancel</Button>
              <Button onClick={() => {
                if (editingTemplate?.id) {
                  setChatTemplates(chatTemplates.map(t => t.id === editingTemplate.id ? editingTemplate : t));
                } else {
                  setChatTemplates([...chatTemplates, { ...editingTemplate, id: Date.now() }]);
                }
                setShowTemplateDialog(false);
              }}>Save Template</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );

  // Announcements Tab - Integrated with Backend
  const [announcements, setAnnouncements] = useState([]);
  const [showAnnouncementDialog, setShowAnnouncementDialog] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [announcementsLoading, setAnnouncementsLoading] = useState(false);

  const fetchAnnouncements = async () => {
    setAnnouncementsLoading(true);
    try {
      const response = await axios.get('/announcements');
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
    } finally {
      setAnnouncementsLoading(false);
    }
  };

  const handleCreateAnnouncement = async () => {
    try {
      if (editingAnnouncement?.id) {
        // Update existing
        await axios.put(`/announcements/${editingAnnouncement.id}`, {
          title: editingAnnouncement.title,
          content: editingAnnouncement.content,
          type: editingAnnouncement.type,
          active: editingAnnouncement.active
        });
        toast.success('Announcement updated');
      } else {
        // Create new
        await axios.post('/announcements', {
          title: editingAnnouncement.title,
          content: editingAnnouncement.content,
          type: editingAnnouncement.type,
          active: editingAnnouncement.active
        });
        toast.success('Announcement created');
      }
      setShowAnnouncementDialog(false);
      fetchAnnouncements();
    } catch (error) {
      toast.error('Failed to save announcement');
    }
  };

  const handleToggleAnnouncement = async (id) => {
    try {
      await axios.patch(`/announcements/${id}/toggle`);
      fetchAnnouncements();
    } catch (error) {
      toast.error('Failed to toggle announcement');
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    try {
      await axios.delete(`/announcements/${id}`);
      toast.success('Announcement deleted');
      fetchAnnouncements();
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  // Fetch announcements when tab is active
  useEffect(() => {
    if (activeTab === 'announcements') {
      fetchAnnouncements();
    }
  }, [activeTab]);

  const renderAnnouncements = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-slate-900">Announcements</h1>
        <Button onClick={() => { setEditingAnnouncement({ title: '', content: '', type: 'info', active: true }); setShowAnnouncementDialog(true); }}>
          <Megaphone className="w-4 h-4 mr-2" />New Announcement
        </Button>
      </div>
      
      {announcementsLoading ? (
        <p className="text-center text-slate-500 py-8">Loading announcements...</p>
      ) : announcements.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Megaphone className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 mb-4">No announcements yet</p>
            <Button onClick={() => { setEditingAnnouncement({ title: '', content: '', type: 'info', active: true }); setShowAnnouncementDialog(true); }} variant="outline">
              Create First Announcement
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {announcements.map(announcement => (
            <Card key={announcement.id} className={`border-l-4 ${
              announcement.type === 'warning' ? 'border-l-amber-500 bg-amber-50' :
              announcement.type === 'error' ? 'border-l-red-500 bg-red-50' :
              'border-l-blue-500 bg-blue-50'
            }`}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Megaphone className={`w-5 h-5 ${
                        announcement.type === 'warning' ? 'text-amber-600' :
                        announcement.type === 'error' ? 'text-red-600' :
                        'text-blue-600'
                      }`} />
                      <p className="font-semibold text-lg">{announcement.title}</p>
                      {announcement.active && <Badge className="bg-green-100 text-green-700">Active</Badge>}
                      {!announcement.active && <Badge className="bg-slate-100 text-slate-500">Inactive</Badge>}
                    </div>
                    <p className="text-slate-600 mb-2">{announcement.content}</p>
                    <p className="text-xs text-slate-400">
                      Posted: {announcement.created_at ? new Date(announcement.created_at).toLocaleDateString() : 'N/A'}
                      {announcement.created_by_name && ` by ${announcement.created_by_name}`}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => { setEditingAnnouncement(announcement); setShowAnnouncementDialog(true); }}>
                      Edit
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleToggleAnnouncement(announcement.id)}
                    >
                      {announcement.active ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteAnnouncement(announcement.id)}>
                      Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      {showAnnouncementDialog && (
        <Dialog open={showAnnouncementDialog} onOpenChange={setShowAnnouncementDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingAnnouncement?.id ? 'Edit Announcement' : 'New Announcement'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Title</Label>
                <Input 
                  value={editingAnnouncement?.title || ''} 
                  onChange={(e) => setEditingAnnouncement({...editingAnnouncement, title: e.target.value})}
                  placeholder="Announcement title"
                />
              </div>
              <div>
                <Label>Type</Label>
                <select 
                  className="w-full border rounded-md p-2"
                  value={editingAnnouncement?.type || 'info'}
                  onChange={(e) => setEditingAnnouncement({...editingAnnouncement, type: e.target.value})}
                >
                  <option value="info">Information</option>
                  <option value="warning">Warning</option>
                  <option value="error">Critical</option>
                </select>
              </div>
              <div>
                <Label>Content</Label>
                <textarea 
                  className="w-full border rounded-md p-2 min-h-[100px]"
                  value={editingAnnouncement?.content || ''}
                  onChange={(e) => setEditingAnnouncement({...editingAnnouncement, content: e.target.value})}
                  placeholder="Announcement content..."
                />
              </div>
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  checked={editingAnnouncement?.active || false}
                  onChange={(e) => setEditingAnnouncement({...editingAnnouncement, active: e.target.checked})}
                />
                <Label>Active (visible to all users)</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAnnouncementDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateAnnouncement}>Save Announcement</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );

  const renderStoppedCases = () => {
    const filteredStoppedCases = getFilteredStoppedCases();
    
    return (
      <div>
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Stopped Cases</h1>
            <p className="text-slate-600">View all stopped verification cases across all clients</p>
          </div>
          <Badge className="bg-red-100 text-red-700 text-lg px-4 py-2">
            {stoppedCases.length} Total Stopped
          </Badge>
        </div>

        {/* Filters */}
        <Card className="border-slate-200 shadow-sm mb-6">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-sm text-slate-600">Stop Reason</Label>
                <Select value={stoppedReasonFilter} onValueChange={setStoppedReasonFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Reasons" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Reasons</SelectItem>
                    <SelectItem value="candidate_no_show">Candidate No Show</SelectItem>
                    <SelectItem value="terminated">Terminated</SelectItem>
                    <SelectItem value="candidate_withdrew">Candidate Withdrew</SelectItem>
                    <SelectItem value="offer_revoked">Offer Revoked</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-slate-600">From Date</Label>
                <Input 
                  type="date" 
                  value={stoppedDateFrom} 
                  onChange={(e) => setStoppedDateFrom(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-sm text-slate-600">To Date</Label>
                <Input 
                  type="date" 
                  value={stoppedDateTo} 
                  onChange={(e) => setStoppedDateTo(e.target.value)}
                />
              </div>
              <div className="flex items-end">
                <Button 
                  variant="outline" 
                  onClick={() => { setStoppedReasonFilter('all'); setStoppedDateFrom(''); setStoppedDateTo(''); }}
                  className="w-full"
                >
                  Clear Filters
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stopped Cases Table */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <StopCircle className="w-5 h-5 text-red-600" />
              Stopped Cases ({filteredStoppedCases.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Case ID</TableHead>
                  <TableHead>Employee</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Stop Reason</TableHead>
                  <TableHead>Stopped At</TableHead>
                  <TableHead>Checks Stopped</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredStoppedCases.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                      <StopCircle className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p>No stopped cases found</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredStoppedCases.map((caseItem) => {
                    const stoppedChecks = (caseItem.checks || []).filter(c => c.status === 'stop_check').length;
                    return (
                      <TableRow key={caseItem.id} className="hover:bg-slate-50">
                        <TableCell className="font-mono text-sm">{caseItem.id}</TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{caseItem.employee_name || 'N/A'}</p>
                            <p className="text-xs text-slate-500">{caseItem.employee_email || ''}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="font-medium">{caseItem.client_name || 'N/A'}</p>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-red-100 text-red-700">
                            {getStopReasonLabel(caseItem.stop_reason)}
                          </Badge>
                          {caseItem.stop_description && (
                            <p className="text-xs text-slate-500 mt-1 max-w-[200px] truncate" title={caseItem.stop_description}>
                              {caseItem.stop_description}
                            </p>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {caseItem.stopped_at ? new Date(caseItem.stopped_at).toLocaleString() : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-slate-100 text-slate-700">{stoppedChecks} checks</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="outline" size="sm" onClick={() => handleViewCase(caseItem.id)}>
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    );
  };

  const isMasterManager = user?.email === 'master@veriflow.com' || masterSession?.user?.email === 'master@veriflow.com';
  
  // Demo accounts removed for production
  const DEMO_ACCOUNTS = [];

  const [switchOpen, setSwitchOpen] = useState(false);
  const [switching, setSwitching] = useState(false);

  const handleSwitchAccount = async (account) => {
    setSwitching(true);
    try {
      // First login to get token and user data
      const response = await axios.post('/auth/login', { 
        email: account.email, 
        password: account.password 
      });
      
      if (isMasterManager && !account.isMaster && onViewAs) {
        // Master Manager viewing as another user
        toast.success(`Now viewing as ${account.role}`);
        setSwitchOpen(false);
        onViewAs(response.data.token, response.data.user);
      } else if (onSwitchAccount) {
        // Regular switch
        localStorage.setItem('token', response.data.token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
        toast.success(`Switched to ${account.role}`);
        setSwitchOpen(false);
        onSwitchAccount(response.data.token, response.data.user);
      }
    } catch (error) {
      toast.error(`Failed to switch: ${error.response?.data?.detail || 'Login failed'}`);
    } finally {
      setSwitching(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar menuItems={menuItems} activeTab={activeTab === 'detail' ? 'dashboard' : activeTab} setActiveTab={setActiveTab} user={user} onLogout={onLogout} onSwitchAccount={onSwitchAccount} hideUserSection={true} groupedMenu={true} />
      <main className="flex-1 overflow-auto">
        {/* Header with User Info, Switch Account, Notification & Logout */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-8 py-3 flex items-center justify-end gap-3">
          {/* Return to Master Button */}
          {isViewingAs && onReturnToMaster && (
            <Button
              onClick={onReturnToMaster}
              variant="outline"
              size="sm"
              className="text-indigo-600 border-indigo-300 hover:bg-indigo-50"
              data-testid="return-to-master"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Return to Master
            </Button>
          )}
          
          {/* Switch/View As Button */}
          {(isMasterManager || onSwitchAccount) && (
            <Popover open={switchOpen} onOpenChange={setSwitchOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2" data-testid="switch-account-button">
                  {isMasterManager ? <Eye className="w-4 h-4" /> : <Users className="w-4 h-4" />}
                  {isMasterManager ? 'View As' : 'Switch'}
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-2" align="end">
                <div className="space-y-1">
                  {isMasterManager && (
                    <p className="text-xs font-medium text-indigo-600 px-2 py-1 bg-indigo-50 rounded mb-2">
                      👁️ Demo Mode - View as user
                    </p>
                  )}
                  {DEMO_ACCOUNTS.map((account) => (
                    <button
                      key={account.email}
                      onClick={() => handleSwitchAccount(account)}
                      disabled={switching || user.email === account.email}
                      className={`w-full flex items-center gap-2 px-2 py-2 rounded text-sm text-left transition-colors ${
                        user.email === account.email 
                          ? 'bg-slate-100 text-slate-400 cursor-not-allowed' 
                          : 'hover:bg-slate-100 text-slate-700'
                      }`}
                    >
                      <span className={`w-2 h-2 rounded-full ${account.color}`}></span>
                      <span className="flex-1">{account.role}</span>
                      {user.email === account.email && <span className="text-xs text-slate-400">(current)</span>}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          )}
          
          <span className="text-sm text-slate-600">{user.name}</span>
          <NotificationCenter />
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/admin/settings')}
            className="text-indigo-600 hover:bg-indigo-50"
            data-testid="admin-settings-button"
          >
            <Cpu className="w-4 h-4 mr-1" />
            AI Settings
          </Button>
          <Button variant="ghost" size="sm" onClick={onLogout} data-testid="logout-button">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
        <div className="p-8 lg:p-10">{renderContent()}</div>
      </main>

      {/* Drill-Down Modal */}
      <Dialog open={showDrillDown} onOpenChange={setShowDrillDown}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              {drillDownTitle}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {drillDownData.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <FileCheck className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>No cases found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Case ID</TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {drillDownData.map((item) => (
                    <TableRow key={item.id} className="hover:bg-slate-50">
                      <TableCell className="font-mono text-sm">{item.id || '-'}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{item.employee_name || '-'}</p>
                          <p className="text-xs text-slate-500">{item.employee_email || ''}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium">{item.client_name || '-'}</p>
                      </TableCell>
                      <TableCell>{getStatusBadge(item.status)}</TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {item.created_at ? new Date(item.created_at).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="outline" size="sm" onClick={() => { setShowDrillDown(false); handleViewCase(item.id); }}>
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowDrillDown(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, subtitle, onClick }) {
  const colors = { blue: 'text-blue-600', emerald: 'text-emerald-600', amber: 'text-amber-600' };
  return (
    <Card className={'border-slate-200 shadow-sm' + (color === 'amber' ? ' border-l-4 border-l-amber-500' : '') + (onClick ? ' cursor-pointer hover:shadow-md transition-shadow' : '')} onClick={onClick}>
      <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2"><Icon className={'w-4 h-4' + (color === 'amber' ? ' text-amber-500' : '')} />{label}</CardTitle></CardHeader>
      <CardContent><div className={'text-3xl font-bold ' + (colors[color] || 'text-slate-900')}>{value}</div>{subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}</CardContent>
    </Card>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <div className="min-w-[200px]">
      <label className="text-sm font-medium text-slate-600 mb-1 block">{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-md bg-white" data-testid={testId}>
        <option value="">All</option>
        {options.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
      </select>
    </div>
  );
}

function TATCard({ type, count, onClick }) {
  const isWithin = type === 'within';
  const isPaused = type === 'paused';
  const isOver = type === 'over';
  
  const config = {
    within: { 
      borderColor: 'border-l-emerald-500', 
      icon: <CheckCircle className="w-5 h-5 text-emerald-500" />,
      textColor: 'text-emerald-600',
      title: 'Within TAT',
      subtitle: 'Cases on track'
    },
    paused: {
      borderColor: 'border-l-orange-500',
      icon: <PauseCircle className="w-5 h-5 text-orange-500" />,
      textColor: 'text-orange-600', 
      title: 'TAT Paused',
      subtitle: 'Cases with insufficiency'
    },
    over: {
      borderColor: 'border-l-rose-500',
      icon: <XCircle className="w-5 h-5 text-rose-500" />,
      textColor: 'text-rose-600',
      title: 'Over TAT',
      subtitle: 'Cases delayed'
    }
  };
  
  const cfg = config[type] || config.within;
  
  return (
    <Card className={`border-slate-200 shadow-sm cursor-pointer hover:shadow-lg transition-shadow border-l-4 ${cfg.borderColor}`} onClick={onClick} data-testid={type + '-tat-card'}>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center justify-between">
          <span className="flex items-center gap-2">{cfg.icon}{cfg.title}</span>
          <ExternalLink className="w-4 h-4 text-slate-400" />
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className={`text-4xl font-bold ${cfg.textColor}`}>{count}</div>
        <p className="text-sm text-slate-500 mt-1">{cfg.subtitle}</p>
        <div className="mt-3 text-xs text-slate-400">Click to view details →</div>
      </CardContent>
    </Card>
  );
}

function DataTable({ title, hint, headers, data, renderRow }) {
  return (
    <Card className="border-slate-200">
      <CardHeader><CardTitle className="text-lg flex items-center gap-2">{title}<span className="text-xs font-normal text-slate-400">{hint}</span></CardTitle></CardHeader>
      <CardContent className="p-0">
        <table className="w-full">
          <thead className="bg-slate-50 border-b"><tr>{headers.map((h, i) => <th key={i} className={'p-4 font-medium text-slate-600 ' + (i === 0 ? 'text-left' : 'text-center')}>{h}</th>)}</tr></thead>
          <tbody className="divide-y">{data.map(renderRow)}{data.length === 0 && <tr><td colSpan={headers.length} className="p-8 text-center text-slate-500">No data available</td></tr>}</tbody>
        </table>
      </CardContent>
    </Card>
  );
}
